'use client'

import type { FormEvent } from 'react'
import { useState } from 'react'
import type { InvestableAssetsRange } from '@nwwp/database'
import { useTrackFormAction, useTrackFormStepViewed } from '@nwwp/segment-analytics'
import { cn, Input } from '@nwwp/ui-shadcn'
import { createSpendCheckLead } from '~/actions/create-spend-check-lead'
import { PhoneDisclosure } from '~/components/LeadMagnet/PhoneDisclosure'
import { NumberInput } from '~/components/NumberInput'
import { RangeSelectInput } from '~/components/RangeSelectInput'
import { parseCurrency } from '~/utils/currency-format'
import { INVESTABLE_ASSETS_OPTIONS } from '~/utils/lead-magnet-qualification'
import { getTrackingParams } from '~/utils/tracking'
import { qualifiesForSpendCheckAdvisor } from '../_lib/qualification'
import { saveUnlockedState, setUnlockCookie } from '../_lib/unlock'
import type { SpendCheckState } from '../types'

const FIELD_LABEL = 'text-subtitle font-bold text-neutral-darker uppercase'

/**
 * Gate shown between the last step and the results.
 *
 * The consent block is the shared PhoneDisclosure, and phone is required because of it:
 * `nwwp_spend-check` is stamped TCPA CONFIRMED at Lead create
 * (packages/salesforce/src/mappers/tcpa-source-map.ts).
 */
export function LeadGate({
  state,
  onBack,
  onSuccess,
}: {
  state: SpendCheckState
  onBack: () => void
  /** `showAdvisorCta` must land in live React state — not only sessionStorage. */
  onSuccess: (showAdvisorCta: boolean) => void
}) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [income, setIncome] = useState(0)
  const [assets, setAssets] = useState<string>('')
  const [phone, setPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showFieldErrors, setShowFieldErrors] = useState(false)
  const trackFormAction = useTrackFormAction()
  const trackFormStepViewed = useTrackFormStepViewed()

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())
  const phoneDigits = phone.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')
  const phoneValid = /^[2-9]\d{9}$/.test(phoneDigits)
  const valid =
    firstName.trim().length > 0 && lastName.trim().length > 0 && emailValid && income > 0 && !!assets && phoneValid

  const err = (condition: boolean, message: string) => (showFieldErrors && condition ? message : undefined)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!valid || submitting) {
      if (!valid) setShowFieldErrors(true)
      return
    }
    setSubmitting(true)
    setSubmitError(null)
    const tracking = getTrackingParams()
    try {
      const created = await createSpendCheckLead(
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          phone: `+1${phoneDigits}`,
          annual_income: income,
          investable_assets_range: assets as InvestableAssetsRange,
        },
        state,
        tracking
      )
      if (!created) throw new Error('Lead creation failed')
      const showAdvisorCta = qualifiesForSpendCheckAdvisor(income, assets)
      // Survives refresh so a return visit is not asked to submit again (duplicate Lead).
      setUnlockCookie()
      saveUnlockedState({
        ...state,
        showAdvisorCta,
      })
      // Scoring fields only — PII stays off Form Action (same as find-your-advisor).
      try {
        trackFormAction({
          questionName: 'annual_income',
          questionValue: income,
          formName: 'worthwhile-spend-check',
          stepName: 'lead',
        })
        trackFormAction({
          questionName: 'investable_assets_range',
          questionValue: assets,
          formName: 'worthwhile-spend-check',
          stepName: 'lead',
        })
        trackFormStepViewed({
          step: 'Submit',
          type: 'form',
          formName: 'worthwhile-spend-check',
          formAction: 'submitted',
          ...tracking,
          investable_assets_range: assets,
        })
      } catch (error) {
        console.warn('Spend Check Form Action tracking failed', { error })
      }
      onSuccess(showAdvisorCta)
    } catch {
      setSubmitError('Something went wrong. Please try again.')
      setSubmitting(false)
    }
  }

  return (
    <form noValidate onSubmit={handleSubmit} className="flex max-w-160 flex-col gap-6">
      <div>
        <h1 className="text-title-md text-gray-darker">Your results are ready</h1>
        <p className="mt-3 text-body text-neutral-darker">
          Share a few details to see how your spending lines up with what you value.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <div className={cn('flex gap-3', 'flex-col tablet:flex-row')}>
          <div className="flex flex-1 flex-col gap-2">
            <label htmlFor="sc-first-name" className={FIELD_LABEL}>
              First Name
            </label>
            <Input
              id="sc-first-name"
              name="first_name"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              error={err(firstName.trim().length === 0, 'Please enter your first name.')}
            />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <label htmlFor="sc-last-name" className={FIELD_LABEL}>
              Last Name
            </label>
            <Input
              id="sc-last-name"
              name="last_name"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              error={err(lastName.trim().length === 0, 'Please enter your last name.')}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="sc-income" className={FIELD_LABEL}>
            Household Income
          </label>
          <NumberInput
            id="sc-income"
            name="annual_income"
            prefix="$"
            decimalScale={0}
            allowNegative={false}
            value={income || ''}
            onChange={(e) => setIncome(parseCurrency(e.target.value))}
            error={err(income <= 0, 'Please enter your household income.')}
          />
        </div>

        <RangeSelectInput
          id="sc-assets"
          name="investable_assets_range"
          label="Investable Assets"
          labelClassName={FIELD_LABEL}
          placeholder="Select"
          value={assets}
          options={INVESTABLE_ASSETS_OPTIONS}
          onChange={setAssets}
          error={err(!assets, 'Please select your investable assets.')}
        />

        <div className="flex flex-col gap-2">
          <label htmlFor="sc-email" className={FIELD_LABEL}>
            Email
          </label>
          <Input
            id="sc-email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="you@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={err(
              !emailValid,
              email.length === 0 ? 'Please enter your email address.' : 'Please enter a valid email address.'
            )}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="sc-phone" className={FIELD_LABEL}>
            Phone Number
          </label>
          <NumberInput
            id="sc-phone"
            name="phone"
            mask="phone"
            placeholder="(555) 555-5555"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={err(
              !phoneValid,
              phone.length === 0 ? 'Please enter your phone number.' : 'Please enter a valid phone number.'
            )}
          />
        </div>
      </div>

      <PhoneDisclosure ctaText="See my results" className="text-body-xs" />

      {submitError ? (
        <p role="alert" className="text-body-sm text-red">
          {submitError}
        </p>
      ) : null}

      <div className="flex flex-col-reverse items-stretch justify-between gap-3 tablet:flex-row tablet:items-center">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="text-body-sm-bold text-green-dark underline disabled:opacity-40"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-full bg-green-lighter-2 px-7 py-3.5 text-body-sm-bold text-black uppercase disabled:opacity-40"
        >
          {submitting ? 'Loading…' : 'See my results'}
        </button>
      </div>
    </form>
  )
}
